import * as React from "react";
import { Input } from "./input";
import { cn } from "../lib/utils";
import { clampToLimit } from "../../utils/fieldLimits";

export interface LimitedInputProps
  extends Omit<React.ComponentProps<"input">, "maxLength"> {
  maxLength: number;
  /** Mostra contatore caratteri sotto il campo (default: true) */
  showCounter?: boolean;
}

function LimitedInput({
  maxLength,
  showCounter = true,
  className,
  value,
  defaultValue,
  onChange,
  onPaste,
  ...props
}: LimitedInputProps) {
  const [truncatedHint, setTruncatedHint] = React.useState(false);
  const hintTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const stringValue =
    value !== undefined && value !== null ? String(value) : undefined;
  const length =
    stringValue?.length ??
    (defaultValue !== undefined ? String(defaultValue).length : 0);
  const atLimit = length >= maxLength;

  const flashTruncated = () => {
    setTruncatedHint(true);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setTruncatedHint(false), 3000);
  };

  React.useEffect(() => {
    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw.length > maxLength) {
      e.target.value = clampToLimit(raw, maxLength);
      flashTruncated();
    }
    onChange?.(e);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    const input = e.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const merged =
      input.value.slice(0, start) + pasted + input.value.slice(end);
    if (merged.length > maxLength) {
      flashTruncated();
    }
    onPaste?.(e);
  };

  return (
    <div className="space-y-1 w-full min-w-0">
      <Input
        {...props}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        aria-invalid={atLimit || truncatedHint ? true : undefined}
        onChange={handleChange}
        onPaste={handlePaste}
        className={cn(
          atLimit && "border-amber-500/60 focus-visible:border-amber-500",
          className,
        )}
      />
      {showCounter && (
        <div className="flex flex-col gap-0.5 px-0.5">
          <p
            className={cn(
              "text-xs text-right tabular-nums",
              atLimit ? "text-amber-400" : "text-muted-foreground",
            )}
            aria-live="polite"
          >
            {length}/{maxLength}
          </p>
          {truncatedHint && (
            <p className="text-xs text-amber-400">
              Testo ridotto: massimo {maxLength} caratteri consentiti.
            </p>
          )}
          {atLimit && !truncatedHint && (
            <p className="text-xs text-amber-400/90">
              Hai raggiunto il limite massimo di caratteri.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { LimitedInput };
